// src/domain/services/taskService.js
import taskRepository from '@/domain/repositories/taskRepository';
import { filterByDateRange } from '@/i12e/utilities/filterHelpers';
import dayjs from 'dayjs';
import { toggleTaskCompletionWithRecurrence } from '@/domain/useCases/tasks/toggleTaskCompletionWithRecurrence';

const taskService = new EventTarget();
let localTaskReadModel = null;
let unsubscribeLocalTaskChanges = () => {};
let lastCachedTasks = null;

const cacheLocalTasks = (tasks) => {
  // Shared server sync can deliver the same snapshot through both the change
  // notification and the initiating call; apply that object only once.
  if (tasks !== lastCachedTasks) {
    taskRepository.replaceCache(tasks);
    lastCachedTasks = tasks;
  }
  return tasks;
};

const reconcileLocalReadModel = () =>
  localTaskReadModel
    ? localTaskReadModel.syncFromServer().then((tasks) => {
        cacheLocalTasks(tasks);
        return tasks;
      })
    : Promise.resolve();

const applyLocalTaskChanges = ({ changes, tasks }) => {
  changes.forEach(({ entity_id: taskId, op }) => {
    if (op === 'delete') {
      taskRepository.removeCachedTask(taskId);
      taskService.dispatchEvent(
        new CustomEvent('tasks:task-deleted', { detail: { id: taskId } })
      );
      return;
    }

    const task = tasks[taskId];
    if (!task) return;

    taskRepository.upsertCachedTask(task);
    taskService.dispatchEvent(
      new CustomEvent(
        op === 'create' ? 'tasks:task-created' : 'tasks:task-updated',
        { detail: task }
      )
    );
  });
};

const configuredLocalReadModel = () => {
  if (!localTaskReadModel) {
    return Promise.reject(new Error('Local task read model is not configured'));
  }
  return Promise.resolve(localTaskReadModel);
};

Object.assign(taskService, {
  /**
   * Retrieves all tasks.
   * @returns {Promise<Object>} - A promise that resolves to the tasks object.
   */
  getAllTasks: () => {
    return taskRepository.getAllTasks();
  },

  /**
   * Registers the local read model for the active task-list lifecycle.
   *
   * This is dependency wiring, not a feature flag: only the selected task
   * flow opts into local reads while adjacent flows keep their current source.
   *
   * @param {Object|null} readModel - Shared local task read model.
   */
  setTaskLocalReadModel: (readModel) => {
    unsubscribeLocalTaskChanges();
    localTaskReadModel = readModel;
    lastCachedTasks = null;
    unsubscribeLocalTaskChanges = readModel?.subscribeChanges
      ? readModel.subscribeChanges((changeSet) => {
          applyLocalTaskChanges(changeSet);
        })
      : () => {};
  },

  /** @returns {Promise<Object>} Persisted task snapshot from LocalDatabase. */
  readLocalTasks: () => {
    return configuredLocalReadModel()
      .then((readModel) => readModel.readLocalTasks())
      .then(cacheLocalTasks);
  },

  /** @returns {Promise<Object>} Change metadata after server synchronization. */
  syncTasksFromServer: () => {
    return configuredLocalReadModel().then((readModel) =>
      readModel.syncChangesFromServer
        ? readModel.syncChangesFromServer()
        : readModel.syncFromServer()
    );
  },

  /** @returns {Promise<Object>} Shared local synchronization state. */
  getTaskSyncState: () =>
    configuredLocalReadModel().then((readModel) => readModel.getSyncState()),

  /**
   * Subscribes to shared task synchronization state changes.
   * @param {Function} listener - State change callback.
   * @returns {Function} Unsubscribe function.
   */
  subscribeTaskSyncState: (listener) => {
    if (!localTaskReadModel) {
      return () => {};
    }
    return localTaskReadModel.subscribeSyncState(listener);
  },

  /**
   * Updates all tasks by fetching from the API.
   * @returns {Promise<Object>} - A promise that resolves to the updated tasks object.
   */
  updateAllTasks: () => {
    return taskRepository
      .updateAllTasks()
      .then((tasks) => {
        taskService.dispatchEvent(
          new CustomEvent('tasks:full-update', { detail: tasks })
        );
        return tasks;
      })
      .catch((error) => {
        console.error('Failed to update all tasks:', error);
        throw error;
      });
  },

  /**
   * Retrieves a task by its ID.
   * @param {number|string} taskId - The ID of the task to retrieve.
   * @returns {Object|null} - The task object or null if not found.
   */
  getTaskById: (taskId) => {
    return taskRepository.getTaskById(taskId);
  },

  /**
   * Creates a new task.
   * @param {string} taskDefinition - The definition of the task to create.
   * @returns {Promise<Object>} - A promise that resolves to the new task object.
   */
  createTask: (taskDefinition) => {
    return taskRepository
      .createTask(taskDefinition)
      .then((newTask) => {
        return reconcileLocalReadModel().then(() => {
          taskService.dispatchEvent(
            new CustomEvent('tasks:task-created', { detail: newTask })
          );
          return newTask;
        });
      })
      .catch((error) => {
        console.error('Failed to create task:', error);
        throw error;
      });
  },

  /**
   * Deletes a task.
   * @param {number|string} taskId - The ID of the task to delete.
   * @returns {Promise<void>} - A promise that resolves when the task is deleted.
   */
  deleteTask: (taskId) => {
    const deletedTask = taskRepository.getTaskById(taskId);
    if (!deletedTask) {
      return Promise.reject(new Error(`Task with ID ${taskId} not found`));
    }
    return taskRepository
      .deleteTask(taskId)
      .then(() => {
        return reconcileLocalReadModel().then(() => {
          taskService.dispatchEvent(
            new CustomEvent('tasks:task-deleted', { detail: deletedTask })
          );
        });
      })
      .catch((error) => {
        console.error('Failed to delete task:', error);
        throw error;
      });
  },

  /**
   * Edits a task's definition.
   * @param {number|string} taskId - The ID of the task to edit.
   * @param {string} definition - The new definition.
   * @returns {Promise<Object>} - A promise that resolves to the updated task object.
   */
  editTaskDefinition: (taskId, definition) => {
    const task = taskRepository.getTaskById(taskId);
    if (!task) {
      return Promise.reject(new Error(`Task with ID ${taskId} not found`));
    }
    return taskRepository
      .updateTask(taskId, { definition })
      .then((updatedTask) => {
        return reconcileLocalReadModel().then(() => {
          taskService.dispatchEvent(
            new CustomEvent('tasks:task-updated', { detail: updatedTask })
          );
          return updatedTask;
        });
      })
      .catch((error) => {
        console.error('Failed to edit task:', error);
        throw error;
      });
  },

  /**
   * Marks a task as completed.
   * @param {number|string} taskId - The ID of the task to mark as completed.
   * @returns {Promise<Object>} - A promise that resolves to the updated task object.
   */
  markTaskAsCompleted: (taskId) => {
    const task = taskRepository.getTaskById(taskId);
    if (!task) {
      return Promise.reject(new Error(`Task with ID ${taskId} not found`));
    }
    if (task.completed_at) {
      return Promise.reject(
        new Error(`Task with ID ${taskId} is already completed`)
      );
    }
    return taskService.toggleTaskCompletion(taskId);
  },

  /**
   * Toggles a task completion state.
   * @param {number|string} taskId - The ID of the task to toggle.
   * @param {{ tzid?: string|null, occurredAt?: string|null }=} options
   * @returns {Promise<Object>} - A promise that resolves to the updated task object.
   */
  toggleTaskCompletion: (taskId, options = {}) => {
    const task = taskRepository.getTaskById(taskId);
    if (!task) {
      return Promise.reject(new Error(`Task with ID ${taskId} not found`));
    }

    return toggleTaskCompletionWithRecurrence({
      taskService,
      taskRepository,
      taskId,
      tzid: options.tzid || null,
      occurredAtIso: options.occurredAt || null
    })
      .then(({ updatedTask, spawnedTask }) => {
        return reconcileLocalReadModel().then(() => {
          taskService.dispatchEvent(
            new CustomEvent('tasks:task-updated', { detail: updatedTask })
          );
          if (spawnedTask && spawnedTask.id !== updatedTask.id) {
            taskService.dispatchEvent(
              new CustomEvent('tasks:task-created', { detail: spawnedTask })
            );
          }
          return updatedTask;
        });
      })
      .catch((error) => {
        console.error('Failed to toggle task completion:', error);
        throw error;
      });
  },

  /**
   * Retrieves all completed tasks within a specific date range.
   * @param {Date} startDate - The start date.
   * @param {Date} endDate - The end date.
   * @returns {Promise<Array>} - An array of completed tasks within the date range.
   */
  getCompletedTasksInRange: (startDate, endDate) => {
    return taskRepository.getAllTasks().then((tasks) => {
      const taskList = Object.values(tasks) || [];
      const start_date = dayjs(startDate);
      const end_date = dayjs(endDate);

      return filterByDateRange(
        taskList.filter((task) => task.completed_at),
        'completed_at',
        start_date,
        end_date
      );
    });
  }
});

export default taskService;
